/**
 * Capability-based permissions (UX-010, DECISIONS §3).
 *
 * The UI asks "can this person do X?" — never "is this person an admin?".
 *
 * Source of truth: the backend catalogue in backend/src/lib/capabilities.ts
 * (F1). Its 43 dotted keys (`quotes.create`, `jobs.complete`,
 * `settings.money`, …) are mirrored in CAPABILITIES below, each with the
 * minimum built-in role that has it by default. A test
 * (src/tests/permissions.f1Contract.test.ts) reads the backend file and fails
 * if the two ever drift.
 *
 * /api/auth/me sends `user.capabilities` as a { key: boolean } map of the
 * person's effective capabilities (custom role, or built-in defaults). When
 * that payload is present it is the ONLY source of truth. Only while it is
 * missing (older session payload) does the built-in role ladder decide
 * (ADMIN > SALES > SUPPORT > VIEWER, using each key's `min`, minus the
 * CAPABILITY_NOT_FOR exceptions).
 *
 * Call sites may use either a dotted F1 key or one of the readable names in
 * CAPABILITY_NAMES (`updateJobStatus` → `jobs.complete`). A readable name can
 * stand for several keys (`viewWork` → any of opportunities/quotes/jobs/
 * invoices view): granted when ANY of them is.
 */

export type Role = 'ADMIN' | 'SALES' | 'SUPPORT' | 'VIEWER'

const ROLE_LEVEL: Record<Role, number> = { VIEWER: 0, SUPPORT: 1, SALES: 2, ADMIN: 3 }

/** Mirror of backend/src/lib/capabilities.ts CAPABILITIES (key → default minimum role). */
export const CAPABILITIES = {
  'dashboard.view': 'VIEWER',
  'contacts.view': 'SUPPORT',
  'contacts.edit': 'SUPPORT',
  'contacts.delete': 'SALES',
  'contacts.files': 'SALES',
  'opportunities.view': 'SALES',
  'opportunities.edit': 'SALES',
  'quotes.view': 'SALES',
  'quotes.create': 'SALES',
  'quotes.send': 'SALES',
  'jobs.view': 'SUPPORT',
  'jobs.edit': 'SALES',
  'jobs.complete': 'SUPPORT',
  'invoices.view': 'SALES',
  'invoices.create': 'SALES',
  'invoices.send': 'SALES',
  'invoices.issue': 'SALES',
  'invoices.void': 'ADMIN',
  'payments.view': 'VIEWER',
  'payments.record': 'SALES',
  'payments.refund': 'ADMIN',
  'payments.correct': 'ADMIN',
  'schedule.view': 'SUPPORT',
  'schedule.edit': 'SALES',
  'inbox.view': 'SUPPORT',
  'inbox.send': 'SALES',
  'calls.view': 'VIEWER',
  'calls.make': 'SALES',
  'marketing.manage': 'SALES',
  'campaigns.manage': 'SALES',
  'campaigns.send': 'ADMIN',
  'forms.manage': 'SALES',
  'workflows.view': 'SALES',
  'workflows.manage': 'ADMIN',
  'reports.view': 'SALES',
  'pricebook.manage': 'SALES',
  'costing.view': 'ADMIN',
  'costing.manage': 'ADMIN',
  'settings.business': 'ADMIN',
  'settings.team': 'ADMIN',
  'settings.phone': 'ADMIN',
  'settings.money': 'ADMIN',
  'settings.integrations': 'ADMIN',
} as const satisfies Record<string, Role>

/**
 * Mirror of the backend `notFor` exceptions: built-in roles that don't get a key
 * by default although they sit above its `min`. Crew (SUPPORT) never sees money
 * (DECISIONS §3, owner-approved 2026-10-20) while Viewer keeps payment status.
 */
export const CAPABILITY_NOT_FOR: Partial<Record<keyof typeof CAPABILITIES, readonly Role[]>> = {
  'payments.view': ['SUPPORT'],
}

/** An F1 capability key. */
export type Capability = keyof typeof CAPABILITIES
export const CAPABILITY_KEYS = Object.keys(CAPABILITIES) as Capability[]

/**
 * Readable names used at call sites → F1 key(s). An array means "any of".
 * Add names here; never invent a dotted key the backend doesn't send.
 */
export const CAPABILITY_NAMES = {
  // Home
  viewDashboard: 'dashboard.view',
  // Contacts
  viewContacts: 'contacts.view',
  viewCompanies: 'contacts.view',
  createContact: 'contacts.edit',
  editContact: 'contacts.edit',
  deleteContact: 'contacts.delete',
  manageContactFiles: 'contacts.files',
  // Inbox & phone
  viewInbox: 'inbox.view',
  sendMessage: 'inbox.send',
  viewCalls: 'calls.view',
  makeCalls: 'calls.make',
  // Work
  viewWork: ['opportunities.view', 'quotes.view', 'jobs.view', 'invoices.view'],
  viewOpportunities: 'opportunities.view',
  createLead: 'opportunities.edit',
  editOpportunity: 'opportunities.edit',
  viewQuotes: 'quotes.view',
  createQuote: 'quotes.create',
  sendQuote: 'quotes.send',
  viewJobs: 'jobs.view',
  scheduleJob: 'jobs.edit',
  updateJobStatus: 'jobs.complete',
  // Money
  viewInvoices: 'invoices.view',
  createInvoice: 'invoices.create',
  sendInvoice: 'invoices.send',
  issueInvoice: 'invoices.issue',
  voidInvoice: 'invoices.void',
  viewPayments: 'payments.view',
  recordPayment: 'payments.record',
  refundPayment: 'payments.refund',
  correctMoney: 'payments.correct',
  // Calendar
  viewCalendar: 'schedule.view',
  manageEvents: 'schedule.edit',
  manageAvailability: 'schedule.edit',
  // Marketing
  viewMarketing: ['marketing.manage', 'campaigns.manage', 'forms.manage'],
  manageReviews: 'marketing.manage',
  manageCampaigns: 'campaigns.manage',
  sendCampaign: 'campaigns.send',
  manageLeadForms: 'forms.manage',
  // Automation & reports
  viewWorkflows: 'workflows.view',
  manageWorkflows: 'workflows.manage',
  viewReports: 'reports.view',
  viewMoneySummary: 'reports.view',
  /** Any money figure at all (mirrors backend lib/moneyVisibility.ts). */
  viewMoney: ['quotes.view', 'invoices.view', 'payments.view', 'reports.view'],
  /** Phone/texting spend meter (GET /api/billing/usage). */
  viewUsage: ['settings.money', 'settings.phone', 'reports.view', 'payments.view'],
  // Job costing (COST-01..03)
  viewCosting: 'costing.view',
  manageCosting: 'costing.manage',
  // Price book
  managePriceBook: 'pricebook.manage',
  // Settings
  manageSettings: 'settings.business',
  manageServiceTypes: 'settings.business',
  manageBookingServices: 'settings.business',
  manageMoneySettings: 'settings.money',
  manageTeam: 'settings.team',
  managePhone: 'settings.phone',
  manageIntegrations: 'settings.integrations',
  /** Any workspace-settings section at all (shows the admin part of Settings). */
  manageAnySettings: ['settings.business', 'settings.team', 'settings.phone', 'settings.money', 'settings.integrations'],
} as const satisfies Record<string, Capability | readonly Capability[]>

export type CapabilityAlias = keyof typeof CAPABILITY_NAMES
export type CapabilityName = Capability | CapabilityAlias

/** Server-provided capabilities: a { key: boolean } map (F1), or a list of granted keys. */
export type CapabilityPayload = readonly string[] | Readonly<Record<string, boolean>>

function isCapability(name: string): name is Capability {
  return Object.prototype.hasOwnProperty.call(CAPABILITIES, name)
}

/** The F1 key(s) behind a name, or [] when unknown. */
export function resolveCapability(name: string): Capability[] {
  if (isCapability(name)) return [name]
  const mapped = (CAPABILITY_NAMES as Record<string, Capability | readonly Capability[]>)[name]
  if (!mapped) return []
  return typeof mapped === 'string' ? [mapped] : [...mapped]
}

/** First F1 key behind a name (kept for callers that need one key). */
export function canonicalCapability(name: string): Capability | null {
  return resolveCapability(name)[0] ?? null
}

const warned = new Set<string>()
function warnUnknown(name: string, where: string) {
  if (!import.meta.env?.DEV || warned.has(name)) return
  warned.add(name)
  // eslint-disable-next-line no-console
  console.warn(`[permissions] Unknown capability "${name}" (${where}). Use an F1 key from backend/src/lib/capabilities.ts or a name in CAPABILITY_NAMES.`)
}

/** Session role ("owner", "admin", "SUPPORT", …) → built-in Role. Owner = ADMIN; unknown = VIEWER. */
export function normalizeRole(role: string | null | undefined): Role {
  const r = (role ?? '').toUpperCase()
  if (r === 'OWNER' || r === 'ADMIN') return 'ADMIN'
  if (r === 'SALES' || r === 'OFFICE') return 'SALES'
  if (r === 'SUPPORT' || r === 'CREW') return 'SUPPORT'
  return 'VIEWER'
}

export function roleAtLeast(role: string | null | undefined, minimum: Role): boolean {
  return ROLE_LEVEL[normalizeRole(role)] >= ROLE_LEVEL[minimum]
}

/** Does built-in `role` have `c` by default? The ladder minus `notFor` (mirrors F1 builtInHas). */
export function builtInHas(role: string | null | undefined, c: Capability): boolean {
  if (CAPABILITY_NOT_FOR[c]?.includes(normalizeRole(role))) return false
  return roleAtLeast(role, CAPABILITIES[c])
}

/** Built-in default capability set for a role (mirrors F1 builtInCapabilities). */
export function builtInCapabilities(role: string | null | undefined): Capability[] {
  return CAPABILITY_KEYS.filter((c) => builtInHas(role, c))
}

/** The granted F1 keys in a payload. Dotted keys resolve directly. */
export function grantedFromPayload(caps: CapabilityPayload): Set<Capability> {
  const out = new Set<Capability>()
  const add = (n: string) => {
    const keys = resolveCapability(n)
    if (keys.length === 0) warnUnknown(n, 'session payload')
    // A readable name in a payload grants exactly its key(s) — only single keys,
    // so an "any of" name can't widen access.
    if (keys.length === 1) out.add(keys[0])
  }
  if (Array.isArray(caps)) {
    for (const n of caps as readonly string[]) add(n)
  } else {
    for (const [n, v] of Object.entries(caps as Record<string, boolean>)) if (v) add(n)
  }
  return out
}

/**
 * The one decision function. With a `capabilities` payload: granted iff the
 * payload grants (any of) the key(s). Without one: the built-in role ladder.
 */
export function can(
  subject: { role?: string | null; capabilities?: CapabilityPayload | null } | null | undefined,
  capability: CapabilityName,
): boolean {
  if (!subject) return false
  const keys = resolveCapability(capability)
  if (keys.length === 0) {
    warnUnknown(capability, 'can()')
    return false
  }
  if (subject.capabilities) {
    const granted = grantedFromPayload(subject.capabilities)
    return keys.some((k) => granted.has(k))
  }
  return keys.some((k) => builtInHas(subject.role, k))
}

/** Short reason for a hidden/disabled action, for helper text and tooltips. */
export function permissionReason(_capability?: CapabilityName): string {
  return "Your role can't do this. Ask an admin if you need access."
}
